<!-- DashBoardView.vue -->
<template>        
    <!-- MAPA PRINCIPAL: Recibe las alertas RECURRENTES -->
    <v-row class="ma-0" v-if="projectName !== 'SmartLinkRAOS'">
        <v-col class="pa-0" cols="12" sm="12" md="12" lg="12" xl="12" xxl="12">
            <v-card class="ma-4 pa-4 mb-0 mt-0 py-0" style="border-radius: 10px;" elevation="0" color="bgtopology">
                <v-card-title>
                    <v-row justify="space-between" class="ma-0" style="align-items: center;">
                        <span class="titleDashboard"> {{ t('labels.linkMap') }} </span>                           
                        <v-progress-circular
                            class="circular_btn"
                            :rotate="360"
                            :size="25"
                            :width="5"
                            :model-value="valorCount"
                            color="bgCircular"
                            @click="restarInfo()"
                        />
                    </v-row>                             
                </v-card-title>
                <v-card-text class="pr-0" :class="statusEquips.length > 0 ? '' : 'text-center'">                        
                    <NetworkChart 
                        v-if="showNetWork" 
                        :lat-lng-sm="mapData.pmpsm"
                        :lat-lng-raj="mapData.rajant"
                        :lat-lng-ap="mapData.pmpap"
                        :polylines-data="mapData.polylines"
                        :rxlvl-snr-data="mapData.rxlvlSnr"
                        :latency-data="mapData.latency"
                        :hexagon-data="mapData.hexagon"
                        :recurrent-alarms="recurrentAlarmsData" 
                    />
                    <Prog_circular v-else />
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>

    <!-- COMPONENTE KPI: Recibe las alarmas NORMALES (Puntuales) -->
    <v-row class="ma-0">
        <v-col cols="12" class="pa-0">
            <KPICOMPONENT 
                v-if="showKPI"
                :count-equip-status="countEquipStatus"
                :alarm-data="normalAlarmsData"
                :operability-data="operabilityData"
            />
        </v-col>
    </v-row>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import type { Ref } from 'vue';

// Store, I18n y Servicios...
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { 
    getAllStatusData, 
    getAlarmDashboard, 
    getAllOperability, 
    getAlarmRecurrent 
} from '@/services/Smartlink/Principal';
import { 
    getLastRXLVLandSNR, 
    getAllLatencyLast, 
    getAllHexagon 
} from '@/services/Smartlink/Map';

import NetworkChart from '@/components/SmartLink/maps/NetworkChart.vue';
import KPICOMPONENT from '@/components/SmartLink/dashboard/KPI-Component.vue';
import Prog_circular from '@/components/PrincipalComponents/Prog_circular.vue';

import { OBJ_ALARM_TYPE } from '@/interfaces/SmartLink/Equipos';
import { LABELS, STRING_GENERAL } from '@/constants/labels';
import axios from 'axios';
import CONSTAPIS, { TIMEOUTTIME } from '@/constants/apis';
import { TOKEN } from '@/constants/labels';

const store = useStore();
const { t } = useI18n();

const projectName = computed(() => store.state.projectName);
const INTERVALRECARGE = projectName.value === 'SmartLinkDEMO' ? (1/3) : (10/6);

// Contenedores de datos desacoplados
const normalAlarmsData = ref<OBJ_ALARM_TYPE>({ distri: [], acceso: [], client: [] });
const recurrentAlarmsData = ref([]);
const operabilityData = ref({
    distribución: { now: 0, today: 0, yesterday: 0, twoDaysAgo: 0 },
    acceso: { now: 0, today: 0, yesterday: 0, twoDaysAgo: 0 },
    cliente: { now: 0, today: 0, yesterday: 0, twoDaysAgo: 0 }
});
const countEquipStatus = ref({
    distri: { ok: 0, alarm: 0, alert: 0, down: 0 },
    acceso: { ok: 0, alarm: 0, alert: 0, down: 0 },
    client: { ok: 0, alarm: 0, alert: 0, down: 0 },
});

const statusEquips = ref([]);
const mapData = ref({
    pmpsm: [], rajant: [], pmpap: [], polylines: [], rxlvlSnr: [], latency: [], hexagon: undefined
});

const showNetWork = ref(true);
const showKPI = ref(false);
const valorCount = ref(0);
const interval = ref();

async function fetchAllDashboardData() {
    try {
        const [
            statusRes, 
            alarmsNormalRes,     // API Normal para KPIs
            recurrentRes,        // API Recurrente para Network
            operabilityRes, 
            rxlvlSnrRes,
            latencyRes,
            hexagonRes,
            pmpsmRes,
            rajantRes,
            pmpapRes,
            polylinesRes
        ] = await Promise.all([
            getAllStatusData(),
            getAlarmDashboard(),   // Normal
            getAlarmRecurrent(),   // Recurrente
            getAllOperability(),
            getLastRXLVLandSNR(),
            getAllLatencyLast(),
            getAllHexagon(),
            axios.get(CONSTAPIS.APIS.value.maps.getAllLatLngSM, { timeout: TIMEOUTTIME, headers: { Authorization: `Bearer ${TOKEN}` } }),
            axios.get(CONSTAPIS.APIS.value.maps.getAllLatLngRAJ, { timeout: TIMEOUTTIME, headers: { Authorization: `Bearer ${TOKEN}` } }),
            axios.get(CONSTAPIS.APIS.value.maps.getAllLatLngAP, { timeout: TIMEOUTTIME, headers: { Authorization: `Bearer ${TOKEN}` } }),
            axios.get(CONSTAPIS.APIS.value.maps.getAllPolylines, { timeout: TIMEOUTTIME, headers: { Authorization: `Bearer ${TOKEN}` } })
        ]);

        // 1. Estados
        if (statusRes.status === 200) {
            countEquipStatus.value = statusRes.data.countEquipData;
            statusEquips.value = statusRes.data.statusEquipData;
            showKPI.value = statusRes.showGraphs;
        }

        // 2. Alertas Normales -> Exclusivo KPI-Component
        if (alarmsNormalRes.status === 200) {
            normalAlarmsData.value = alarmsNormalRes.data;
        }

        // 3. Alertas Recurrentes -> Exclusivo NetworkChart (MessageComponent)
        if (recurrentRes.status === 200) {
            recurrentAlarmsData.value = recurrentRes.data;
        }

        // 4. Operatividad
        if (operabilityRes.status === 200) {
            operabilityData.value = operabilityRes.data;
        }

        // 5. Data del Mapa
        mapData.value = {
            rxlvlSnr: rxlvlSnrRes.status === 200 ? rxlvlSnrRes.data : [],
            latency: latencyRes.status === 200 ? latencyRes.data : [],
            hexagon: hexagonRes.status === 200 ? hexagonRes.data : null,
            pmpsm: pmpsmRes.data || [],
            rajant: rajantRes.data || [],
            pmpap: pmpapRes.data || [],
            polylines: polylinesRes.data || []
        };

        // Guardar estado general de KPIs en Store
        store.commit('setTotalKPI', {
            distri: { valor: STRING_GENERAL.distri, alarm: normalAlarmsData.value.distri, total: operabilityData.value.distribución, count: countEquipStatus.value.distri },
            acceso: { valor: STRING_GENERAL.access, alarm: normalAlarmsData.value.acceso, total: operabilityData.value.acceso, count: countEquipStatus.value.acceso },
            client: { valor: STRING_GENERAL.client, alarm: normalAlarmsData.value.client, total: operabilityData.value.cliente, count: countEquipStatus.value.client }
        });

    } catch (error) {
        store.commit('showSnackBar', { type: LABELS.error, msg: t('snackbar.errorApis'), valor: true });
    }
}

async function restarInfo() {
    valorCount.value = 0;
    await fetchAllDashboardData();
}

onMounted(async () => {
    await fetchAllDashboardData();
    interval.value = setInterval(async () => {
        if (valorCount.value >= 100) {
            await restarInfo();
        } else {
            valorCount.value += INTERVALRECARGE;
        }
    }, 1000);
});

onBeforeUnmount(() => {
    if (interval.value) clearInterval(interval.value);
});
</script>


<style>

.cardSingleKPI{
    cursor: pointer;
}

.btnKPI{
    border-radius: 5px;
}

.mapdiv {
    border-radius: 10px !important;
}

.dashboard-card .v-btn--active{
    background-color: rgb(var(--v-theme-btnSelect)) !important;
}

/* .dashboard-card .titleDashboard{
    font-family: 'Tilt Warp',sans-serif;
    font-size: 30px;
} */
 
.borderMap{
    border-radius: 10px !important;
}

.defaultCard{
    border-radius: 10px !important;
    padding: 16px !important;
    margin: 16px !important;
}

.textappBAR{    
  font-weight: bold;
  text-transform: capitalize !important;
  font-size: 19px !important;  
  font-family: 'Poppins', sans-serif;
  letter-spacing: 5px;
}

.titleDashboard{
    font-family: 'Poppins',sans-serif;
    font-size: 30px;
    font-weight: 400;
    color: rgb(var(--v-theme-colorText));
}

.subtitleDashboard{
    font-family: 'Poppins',sans-serif;
    font-size: 20px;
    font-weight: 300;
    color: rgb(var(--v-theme-colorText));
}

.textDashboard{
    font-family: 'Poppins',sans-serif;
    font-size: 14px;
    color: rgb(var(--v-theme-colorText));
}

.textblackDashboard{
    font-family: 'Poppins',sans-serif;
    font-size: 14px;
    color: black;
}

.btnSuccessDashboard{
    background-color: #0098f0 !important;
    background-image: linear-gradient(to bottom left, #00f2c3, #0098f0,#00f2c3) !important;
    border-radius: 5px;
    transition: .3s ease-in-out;
}
.btnSuccessDashboard:hover{
    transform: translateY(-1px);    
    background-position: 0 100%;
    transition: .3s ease-in-out;
    box-shadow: 2px 2px 6px rgba(0, 0, 0, .4);
}

.btnSlidersDashboard{
    /* background: #1D8CF8; */
    background-size: 210% 210%;
    background-position: 100% 0;
    background-color: transparent;
    transition: all .15s ease;
    box-shadow: none;
    /* color: #fff; */
}

.text_btns{
    font-family: 'Poppins',sans-serif;
    font-size: 13px;
    font-weight: 400;
    margin: 5px;
    /* color: white; */
    color:  rgb(var(--v-theme-colorText));
}


.dashboard-card .v-btn--active .v-btn__overlay{
    background-color: rgb(var(--v-theme-btnSelect))!important;
}

/* .v-slide-group__next, .v-slide-group__prev{
    min-width: 0px !important;
} */

.dashboard-card{
    border: 2px solid rgba(var(--v-theme-colorBorder),0.1);
    border-radius: 20px;
 }

 .operability-card{
    border: 2px solid rgba(var(--v-theme-colorBorder),0.1);
 }

/* .dashboard-card .v-card-title{
    background-color:rgba(var(--v-theme-bgDashCard)) !important;
 } */

.card-dialogs{
    z-index: +3;
    background-color: rgba(var(--v-theme-blackAndWhite), 0.85) !important;
    border-radius: 20px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    max-height: 300px !important;
 }

.circular_btn:hover{
    cursor:pointer;
 }

.text_ellipsis{
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.alertNumber{
    margin: 10px auto;
    border-radius: 15px;
    color: black !important;
    padding: 7px 3px;
    transition: 0.5s;
}

.alertNumber:hover{
    cursor: pointer;
    transform: scale(1.1);
    transition: 0.5s;
}

.v-label--clickable, .v-selection-control__wrapper, .v-input__control, 
.v-slide-group__next, .v-slide-group__prev {    
    color: rgb(var(--v-theme-colorText)) !important;
}

.appBarComp .v-input__control{
    color: rgba(var(--v-theme-colorText)) !important;
}

.v-input__control .v-label{
    font-family: 'Poppins',sans-serif;
    font-size: 15px;
    color: rgb(var(--v-theme-colorText));
}

.v-slider .v-input__prepend, .v-slider .v-input__append{
    color: rgb(var(--v-theme-colorText)) !important;
}

.v-input__control .v-selection-control--density-default{
    min-height: 0px !important;
}
</style>